import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useUser } from "@/modules/users/hooks/useUser"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import {
  ProgramType,
  type SharedCoach,
  SharedCoachRequestAction,
  SharedCoachStatus,
} from "@modules/programs/types/programTypes"
import { SharedCoachView } from "@modules/sharedCoach/components/sharedCoachView"
import { useSharedCoachRequest } from "@modules/sharedCoach/hooks/useSharedCoachActions"

type SharedCoachRequestProps = {
  programId: string
  currentSharedCoach?: SharedCoach
  className?: string
} & React.ComponentProps<"div">

export function SharedCoachRequest({ programId, currentSharedCoach, className, ...props }: SharedCoachRequestProps) {
  const user = useUser()
  const mySpaceNavigate = useMySpaceNavigate()
  const { data: program } = useProgram(programId)
  const navigateTo = (action: SharedCoachRequestAction) => {
    const mySpace = Path.MySpace
    switch (action) {
      case SharedCoachRequestAction.ACCEPT:
      case SharedCoachRequestAction.REMOVE:
        return program.type === ProgramType.VIRTUAL
          ? mySpace.ProgramDetails(programId)
          : mySpace.ProgramOnOffDetails(programId)
      case SharedCoachRequestAction.REFUSE:
      case SharedCoachRequestAction.QUIT:
        return mySpace.Training
    }
  }
  const { requestSharedCoach } = useSharedCoachRequest()
  const requestSharedCoachActions = async (action: SharedCoachRequestAction) => {
    if (user?.id) {
      await requestSharedCoach(program.id, action, user.id)
        .then(() => {
          toast.success(word(`program.sharedCoach.action.request.${action}.success`), {
            classNames: successToastClassName(),
          })
          mySpaceNavigate(navigateTo(action))
        })
        .catch(() => {
          toast.error(word(`program.sharedCoach.action.request.error`), {
            classNames: errorToastClassName(),
          })
        })
    }
  }

  if (currentSharedCoach?.status === SharedCoachStatus.PENDING) {
    return (
      <div className={cn("mb-3 flex flex-col gap-3 rounded-4xl border-none bg-neutral-100 p-4", className)} {...props}>
        <Text fontStyle="BL">{word("program.sharedCoach.action.title")}</Text>
        <Text fontStyle="BM" className="text-textDefault-caption">
          {word("program.sharedCoach.requested", {
            firstname: program.owner?.firstname ?? "",
            lastname: program.owner?.lastname ?? "",
          })}
        </Text>
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={() => void requestSharedCoachActions(SharedCoachRequestAction.REFUSE)}
          >
            {word("program.sharedCoach.action.refuse")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="flex-1"
            onClick={() => void requestSharedCoachActions(SharedCoachRequestAction.ACCEPT)}
          >
            {word("program.sharedCoach.action.accept")}
          </Button>
        </div>
      </div>
    )
  } else if (currentSharedCoach?.status === SharedCoachStatus.ACCEPTED) {
    return (
      <div
        className={cn("mb-3 flex flex-col gap-3 rounded-4xl border-none bg-surface-foreground p-4", className)}
        {...props}
      >
        <Text fontStyle="BL">{word("program.sharedCoach.action.title")}</Text>
        <Text fontStyle="BM" className="text-textDefault-caption">
          {`${word("program.sharedCoach.isCurrent")} ${program.sharedCoaches && program.sharedCoaches.length > 1 ? `${word("global.with")} :` : ""}`}
        </Text>
        {program.sharedCoaches
          ?.filter((coach) => coach.user.id !== user?.id && coach.status === SharedCoachStatus.ACCEPTED)
          .map((coach) => (
            <SharedCoachView key={coach.user.id} profile={coach.user} status={coach.status} />
          ))}
        <Button
          variant="outline"
          size="lg"
          onClick={() => void requestSharedCoachActions(SharedCoachRequestAction.QUIT)}
        >
          {word("program.sharedCoach.action.quit")}
        </Button>
      </div>
    )
  } else {
    return null
  }
}
